/**
 * SEOHead — componente de meta tags dinámico para MANDO
 * Usa react-helmet-async para SSR-compatible meta injection.
 *
 * Uso:
 *   <SEOHead
 *     title="Software para Reformas | MANDO"
 *     description="..."
 *     canonical="https://mando.leapifyes.com/precios"
 *     schema={softwareAppSchema}
 *     noindex={false}
 *   />
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const BASE_URL  = "https://mando.leapifyes.com";
const BASE_OG   = "https://res.cloudinary.com/dnjsjo6yx/image/upload/v1770925218/MANDO_by_Leapifyes_tajoao.png";
const SITE_NAME = "MANDO by Leapifyes";
const TWITTER   = "@leapifyes";

export function SEOHead({
  title,
  description,
  canonical,
  ogImage   = BASE_OG,
  ogType    = "website",
  noindex   = false,
  schema    = null,          // JSON-LD object or array
  extraMeta = [],
}) {
  const fullTitle = title || "Software de Gestión de Obras y Reformas | MANDO ERP SaaS España";
  const fullDesc  = description ||
    "MANDO es el ERP SaaS para empresas de reformas y obras. Presupuestos, margen por obra, facturas e IA en tiempo real. Prueba gratis 15 días, sin tarjeta.";
  const robots    = noindex ? "noindex,nofollow" : "index,follow";

  /* Serialise schema — admite objeto o array de schemas */
  const schemaStr = schema
    ? JSON.stringify(Array.isArray(schema) ? schema : [schema])
    : null;

  return (
    <Helmet>
      {/* ── Básicos ── */}
      <html lang="es" />
      <title>{fullTitle}</title>
      <meta name="description"                content={fullDesc} />
      <meta name="robots"                     content={robots} />
      {canonical && <link rel="canonical"     href={canonical} />}

      {/* hreflang */}
      {canonical && <link rel="alternate" hrefLang="es-ES" href={canonical} />}
      {canonical && <link rel="alternate" hrefLang="es"    href={canonical} />}
      {canonical && <link rel="alternate" hrefLang="x-default" href={canonical} />}

      {/* ── Open Graph ── */}
      <meta property="og:type"        content={ogType} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={fullDesc} />
      <meta property="og:url"         content={canonical || BASE_URL} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"      content="es_ES" />
      <meta property="og:site_name"   content={SITE_NAME} />

      {/* ── Twitter ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER} />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={fullDesc} />
      <meta name="twitter:image"       content={ogImage} />

      {/* ── Extra metas opcionales ── */}
      {extraMeta.map((m, i) => <meta key={i} {...m} />)}

      {/* ── Schema.org JSON-LD ── */}
      {schemaStr && (
        <script type="application/ld+json">{schemaStr}</script>
      )}
    </Helmet>
  );
}

/* ─── SCHEMAS PRE-CONSTRUIDOS ────────────────────────────────────────────── */

export const SCHEMAS = {
  /** SoftwareApplication — para la home */
  softwareApp: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "MANDO",
    "alternateName": "MANDO ERP",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "ERP, Gestión de Obras, Reformas",
    "operatingSystem": "Web, iOS, Android",
    "url": "https://mando.leapifyes.com",
    "description": "ERP SaaS especializado en empresas de reformas, obras y mantenimiento. Presupuestos profesionales, control de margen por obra, facturación automática e inteligencia artificial integrada.",
    "screenshot": "https://res.cloudinary.com/dnjsjo6yx/image/upload/v1770925218/MANDO_by_Leapifyes_tajoao.png",
    "featureList": [
      "Presupuestos PDF profesionales",
      "Control de margen por obra en tiempo real",
      "Facturación automática",
      "OCR de facturas con IA",
      "Chat IA con acceso a tus datos",
      "Control de stock y materiales",
      "Gestión de incidencias",
      "Baremos de mercado",
      "Dashboard KPIs en tiempo real",
      "App móvil iOS y Android"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "59",
      "highPrice": "299",
      "offerCount": "4",
      "offers": [
        { "@type": "Offer", "name": "START", "price": "59",  "priceCurrency": "EUR", "description": "1 usuario, funcionalidades básicas" },
        { "@type": "Offer", "name": "PRO",   "price": "119", "priceCurrency": "EUR", "description": "5 usuarios + IA incluida" },
        { "@type": "Offer", "name": "BUSINESS","price": "179","priceCurrency": "EUR","description": "8 usuarios + funciones avanzadas" },
        { "@type": "Offer", "name": "ELITE", "price": "299", "priceCurrency": "EUR", "description": "Usuarios ilimitados + SLA" }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "bestRating": "5",
      "worstRating": "1",
      "reviewCount": "127"
    },
    "provider": {
      "@type": "Organization",
      "name": "Leapifyes",
      "alternateName": "DigitalLeap Solutions S.L.",
      "url": "https://leapifyes.com",
      "logo": "https://res.cloudinary.com/dnjsjo6yx/image/upload/v1770925218/MANDO_by_Leapifyes_tajoao.png",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Barcelona",
        "addressCountry": "ES"
      }
    }
  },

  /** Organization — para todas las páginas */
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Leapifyes",
    "alternateName": "DigitalLeap Solutions S.L.",
    "url": "https://leapifyes.com",
    "logo": "https://res.cloudinary.com/dnjsjo6yx/image/upload/v1770925218/MANDO_by_Leapifyes_tajoao.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+34-694-214-849",
      "contactType": "customer service",
      "areaServed": "ES",
      "availableLanguage": ["Spanish", "Catalan"]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Barcelona",
      "addressLocality": "Barcelona",
      "addressRegion": "Cataluña",
      "addressCountry": "ES"
    },
    "sameAs": [
      "https://www.linkedin.com/company/leapifyes"
    ]
  },

  /** FAQPage — para sección FAQ de la home */
  faq: (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a }
    }))
  }),

  /** BreadcrumbList — para páginas internas */
  breadcrumb: (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map(({ name, url }, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": name,
      "item": url
    }))
  }),

  /** Article — para blog posts */
  article: ({ title, description, url, datePublished, dateModified, image }) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": url,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "image": image,
    "author": {
      "@type": "Organization",
      "name": "Leapifyes",
      "url": "https://leapifyes.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MANDO by Leapifyes",
      "logo": {
        "@type": "ImageObject",
        "url": "https://res.cloudinary.com/dnjsjo6yx/image/upload/v1770925218/MANDO_by_Leapifyes_tajoao.png"
      }
    }
  }),
};

export default SEOHead;
